import React, { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { AlertTriangle, Mail, RefreshCw, XCircle } from 'lucide-react';
import { getInvites, revokeInvite } from '../services/iam';
import { UserInvite } from '../types/iam';
import { toast } from '@/lib/toast';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { Button, Card, Chip, EmptyState, OutcomeBadge, PaginationBar, Select, TanstackTable, Timestamp } from '@/components/ds-zta';
import { ROWS_PER_PAGE_OPTIONS, pageOffset } from '@/lib/urlPaging';
import { apiErrorMessage } from './modals/iamForm';

const columnHelper = createColumnHelper<UserInvite>();

interface Props {
  /** pending | accepted | expired | revoked, or 'all'. */
  status: string;
  page: number;
  rows: number;
  onStatusChange: (status: string) => void;
  onPageChange: (page: number) => void;
  onRowsChange: (rows: number) => void;
  onInvite?: () => void;
}

// InvitesPanel lists the workspace's invites (admin-only on the backend) and
// lets an administrator revoke a pending one. Paging and the status filter are
// owned by the Users page, which keeps them in the URL.
const InvitesPanel: React.FC<Props> = ({ status, page, rows, onStatusChange, onPageChange, onRowsChange, onInvite }) => {
  const queryClient = useQueryClient();
  const { confirm: confirmDialog } = useDialogs();

  const { data, isLoading, isError, error, refetch } = useQuery(
    ['invites', status, page, rows],
    () => getInvites({ status: status === 'all' ? undefined : status, limit: rows, offset: pageOffset(page, rows) }),
    { keepPreviousData: true }
  );

  const revoke = useMutation((invite: UserInvite) => revokeInvite(invite.id), {
    onSuccess: (_, invite) => {
      queryClient.invalidateQueries('invites');
      toast.success(`Invite for ${invite.email} revoked`);
    },
    onError: (err) => {
      toast.error(`Failed to revoke invite: ${apiErrorMessage(err)}`);
    },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor('email', {
        header: 'Email',
        meta: { sortable: true },
        cell: (info) => <span className="text-dense font-medium text-[var(--d2d-text-primary)]">{info.getValue()}</span>,
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        meta: { sortable: true },
        cell: (info) => <OutcomeBadge value={info.getValue()}>{info.getValue()}</OutcomeBadge>,
      }),
      columnHelper.display({
        id: 'access',
        header: 'Role and groups',
        cell: (info) => {
          const inv = info.row.original;
          const roles = inv.roles || [];
          const groups = inv.groups || [];
          return (
            <div className="flex flex-wrap gap-1">
              {roles.length === 0 ? (
                <Chip variant="neutral">Viewer</Chip>
              ) : (
                roles.map((r) => <Chip key={r.id} variant="neutral">{r.name}</Chip>)
              )}
              {groups.map((g) => (
                <Chip key={g.id} variant="neutral">{g.name}</Chip>
              ))}
            </div>
          );
        },
      }),
      columnHelper.accessor('invited_at', {
        header: 'Invited',
        meta: { sortable: true },
        cell: (info) => <Timestamp value={info.getValue()} noSeconds className="text-dense text-[var(--d2d-text-muted)]" />,
      }),
      columnHelper.display({
        id: 'expires',
        header: 'Expires / accepted',
        cell: (info) => {
          const inv = info.row.original;
          return (
            <Timestamp
              value={inv.status === 'accepted' ? inv.accepted_at : inv.expires_at}
              noSeconds
              className="text-dense text-[var(--d2d-text-muted)]"
            />
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <span className="block text-right">Actions</span>,
        cell: (info) => {
          const inv = info.row.original;
          if (inv.status !== 'pending') return null;
          return (
            <div className="flex justify-end">
              <Button
                variant="secondary"
                size="sm"
                leadingIcon={<XCircle className="h-4 w-4" />}
                disabled={revoke.isLoading}
                onClick={async () => {
                  const ok = await confirmDialog({
                    title: `Revoke invite for ${inv.email}?`,
                    message: `${inv.email} will no longer be able to join this workspace by signing in. You can invite them again later.`,
                    confirmLabel: 'Revoke',
                    variant: 'danger',
                  });
                  if (ok) revoke.mutate(inv);
                }}
              >
                Revoke
              </Button>
            </div>
          );
        },
      }),
    ],
    [revoke, confirmDialog]
  );

  const invites = data?.data || [];
  const total = data?.total || 0;
  // TanStack Table is not memoizable by the React Compiler; every list page
  // carries the same exception (in eslint-suppressions.json).
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({ data: invites, columns, getCoreRowModel: getCoreRowModel() });

  const empty = isError ? (
    <EmptyState
      icon={<AlertTriangle />}
      title="Couldn't load invites"
      description={apiErrorMessage(error)}
      action={
        <Button size="sm" variant="secondary" leadingIcon={<RefreshCw className="h-4 w-4" />} onClick={() => refetch()}>
          Retry
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={<Mail />}
      title={status !== 'all' ? `No ${status} invites` : 'No invites yet'}
      description="Invite someone by email; they join with the role you choose the first time they sign in."
      action={
        onInvite ? (
          <Button size="sm" leadingIcon={<Mail className="h-4 w-4" />} onClick={onInvite}>
            Invite User
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="sm:w-44">
        <Select
          className="gap-0"
          label={<span className="sr-only">Invite status</span>}
          value={status}
          onChange={(e) => onStatusChange(e.target.value)}
        >
          <option value="all">All invites</option>
          <option value="pending">Pending</option>
          <option value="accepted">Accepted</option>
          <option value="expired">Expired</option>
          <option value="revoked">Revoked</option>
        </Select>
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <TanstackTable aria-label="Invites" table={table} loading={isLoading} empty={empty} />
        </div>
      </Card>
      {(total || invites.length) > 0 && (
        <PaginationBar
          page={page}
          totalRows={total || invites.length}
          rowsPerPage={rows}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsChange}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        />
      )}
    </div>
  );
};

export default InvitesPanel;
